import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

/**
 * The profile a nested editor should edit (ECH-387).
 *
 * Thirteen editors read `location.state.localProfile` and dereference it in
 * their `useState` initialisers. Router state exists only when the editor was
 * opened from the Profile UI, so a reload or a pasted link crashed with
 * `Cannot read properties of undefined (reading 'demographicDetails')` and left
 * a blank frame. QA reproduced it on 13 routes across all four roles, 52/52.
 *
 * Each editor already carried an `if (!localProfile)` guard, and it could never
 * run: it sat below the initialisers that crashed first. Hoisting it above them
 * is not an option either, because returning before the hooks would change the
 * hook order between renders.
 *
 * ## Router state is a cache, not the source
 *
 * `localProfile` is the canonical profile contract from
 * `state.profileSlice.profile`. Profile data is under `localProfile.profile`
 * and demographics under `localProfile.demographics`.
 *
 * Router state still wins when present, because that working copy can hold
 * edits the member has made and not yet saved.
 *
 * ## Why `isReady` is separate from `profile`
 *
 * A direct load races the profile fetch, which `AppContent` dispatches on
 * mount. "Not loaded yet" and "no profile" would otherwise be the same value,
 * and a caller that cannot tell them apart either renders an empty editor over
 * real data or bounces the member away from a URL that was about to work.
 */
export const useEditableProfile = () => {
  const location = useLocation();
  const stored = useSelector((state) => state.profileSlice?.profile ?? null);

  const profile = location.state?.localProfile ?? stored ?? null;

  return { profile, isReady: Boolean(profile) };
};

export default useEditableProfile;
